import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import types from 'frontend/state/actionTypes';
import {
  inputSource,
  getRawCombinations,
  transformInputToData,
  makeGearOptions,
  isGearValidForOptimize,
  getSelectedGear,
} from 'shared/combinations';
import { inputInfo } from 'shared/addonInput';
import SimcInput from 'shared/simcinput';
import { isUnsupportedHealer } from 'shared/character';
import { itemSocketInfo, slotMap } from 'shared/items';
import TalentTraits from 'shared/talentTraits';
import GameData from 'shared/gameData';

import { toggleReducer, setterReducer } from 'frontend/state/utils';

import gemEnchantReducer, { selectors as gemEnchantSelectors } from './gemEnchant';
import talentTreesReducer, { selectors as talentTreeSelectors } from './talentTrees';
import enhancementsReducer, { selectors as enhancementsSelectors } from './enhancements';
import downloadReducer from './download';

const textReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // INPUT_OPTIMIZE_TEXT is for user text entry and component loading actions
    case types.INPUT_OPTIMIZE_TEXT:
      return payload.input;
    // SET_OPTIMIZE_TEXT is used for preloading data from a report page, triggers a different set
    // of actions on the Optimize Input component and avoids a full reload
    case types.SET_OPTIMIZE_TEXT:
      return payload;
    case types.LOAD_OPTIMIZE_REPORT:
      return SimcInput.stringify(payload.optimize.data);
    default:
      return state;
  }
};
const preloadDataReducer = (state = [], action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // load from report
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.data;
    // clear once text is forcibly set
    case types.SET_OPTIMIZE_TEXT:
    case types.INPUT_OPTIMIZE_TEXT:
      return '';
    default:
      return state;
  }
};

const combinationsReducer = (state = null, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.FETCH_COMBINATIONS_SUCCESS:
      return payload;
    default:
      return state;
  }
};

const optimizeSlotMap = {
  finger: 'rings',
  trinket: 'trinkets',
};

const gearReducer = (state = {}, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT:
    case types.FETCH_GEAR_INFO_REQUEST:
      return {};
    case types.FETCH_GEAR_INFO_SUCCESS:
      return payload;
    case types.ADD_OPTIMIZE_ITEM: {
      const { slot, item } = payload;
      return _.mapValues(state, (items, key) => {
        if (key === slot) {
          return [
            ...items,
            {
              ...item,
              customItem: true,
            },
          ];
        }

        return items;
      });
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.allGear;
    case types.FETCH_OPTIMIZE_CUSTOM_ITEMS_SUCCESS: {
      const { items } = payload;
      const itemsGrouped = _.groupBy(items, item => {
        const slot = slotMap[item.inventoryType];
        return optimizeSlotMap[slot] || slot;
      });

      return _.mapValues(state, (stateItems, slot) => {
        if (itemsGrouped[slot]) {
          return [
            ...stateItems,
            ..._.map(itemsGrouped[slot], item => ({
              ...item,
              customItem: true,
            })),
          ];
        }
        return stateItems;
      });
    }
    default:
      return state;
  }
};

const loadingReducer = (state = false, action = {}) => {
  const { type } = action;
  switch (type) {
    case types.SET_OPTIMIZE_LOADING:
    case types.FETCH_COMBINATIONS_REQUEST:
    case types.FETCH_GEAR_INFO_REQUEST:
      return true;
    case types.FETCH_COMBINATIONS_SUCCESS:
    case types.FETCH_COMBINATIONS_FAILURE:
    case types.FETCH_GEAR_INFO_SUCCESS:
    case types.FETCH_GEAR_INFO_FAILURE:
    case types.RESET_OPTIMIZE:
      return false;
    default:
      return state;
  }
};

const selectedReducer = (state = {}, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.FETCH_GEAR_INFO_SUCCESS: {
      return _.mapValues(payload, items =>
        _.map(
          _.filter(
            _.map(items, (it, idx) => ({ ...it, origIdx: idx })),
            item => item.equipped,
          ),
          item => item.origIdx,
        ),
      );
    }
    case types.TOGGLE_COMBINATION_ITEM: {
      const { slot, index } = payload;
      if (state[slot]) {
        return _.mapValues(state, (selectedItems, selectedSlot) => {
          if (selectedSlot === slot) {
            if (_.includes(selectedItems, index)) {
              return _.without(selectedItems, index);
            }
            return [...selectedItems, index];
          }
          return selectedItems;
        });
      }

      return {
        ...state,
        [slot]: [index],
      };
    }
    case types.SELECT_OPTIMIZE_ITEMS: {
      const { items } = payload;
      const newState = { ...state };
      _.each(items, (entry) => {
        const { slot, index } = entry;
        if (newState[slot]) {
          if (!_.includes(newState[slot], index)) {
            newState[slot] = [
              ...newState[slot],
              index,
            ];
          }
        } else {
          newState[slot] = {
            [slot]: [index],
          };
        }
      });
      return newState;
    }
    case types.ADD_OPTIMIZE_ITEM: {
      const { slot, selectedIndex } = payload;
      const newState = { ...state };
      newState[slot] = [
        ...newState[slot],
        selectedIndex,
      ];
      return newState;
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.selected;
    default:
      return state;
  }
};

const lockReducer = (reducerSlot, loadField) => (state = null, action = {}) => {
  const { type, payload } = action;

  // reset to default on change
  if (type === types.INPUT_OPTIMIZE_TEXT) {
    return null;
  }

  // this needs to happen before the slot check since this payload won't have the slot
  if (type === types.LOAD_OPTIMIZE_REPORT) {
    return _.get(payload.optimize, loadField);
  }

  if (!payload) {
    return state;
  }

  const { slot, idx } = payload;
  if (slot !== reducerSlot) {
    return state;
  }

  switch (type) {
    case types.TOGGLE_LOCK:
      // unset on repeat click
      if (state === idx) {
        return null;
      }
      return idx;
    case types.TOGGLE_COMBINATION_ITEM:
      // clear on item toggle
      if (payload.index === state) {
        return null;
      }
      return state;
    default:
      return state;
  }
};

const hideLowLevelItemsReducer = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_HIDE_LOW_LEVEL,
  initial: true,
  reset: types.RESET_OPTIMIZE,
});
const enhancementAdvancedControls = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_ENHANCEMENT_ADVANCED_CONTROLS,
  initial: false,
  reset: types.RESET_OPTIMIZE,
});
const enhancementReplace = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_ENHANCEMENT_REPLACE,
  initial: false,
  reset: types.RESET_OPTIMIZE,
  custom: (state, action) => {
    const { type, payload } = action;
    switch (type) {
      case types.LOAD_OPTIMIZE_REPORT:
        return payload.optimize.enhancementReplace;
      default:
        return state;
    }
  },
});

const upgradeCurrencyConstraints = (state = {}, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT: {
      return _.fromPairs(_.map(payload.character?.upgradeCurrencies, c => ([c.id, c.amount])));
    }
    case types.SET_UPGRADE_CURRENCY_CONSTRAINT:
      return {
        ...state,
        [payload.currency]: payload.amount,
      };
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize?.upgradeCurrencyConstraints || {};
    case types.RESET_OPTIMIZE:
      return {};
    default:
      return state;
  }
};

const itemSetRequirements = (state = {}, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.FETCH_GEAR_INFO_SUCCESS: {
      // Make sure we have enough pieces to satisfy the constraint.
      // It may have been set higher in a previous sim from a different character.
      // If there aren't enough pieces, set the constraint back to 0
      const flatItems = _.flatten(_.values(payload));
      const itemIds = new Set(_.map(flatItems, 'id'));
      return _.mapValues(state, (num, itemSetId) => {
        const itemSet = _.find(GameData.ItemSets, { id: parseInt(itemSetId, 10) });
        if (itemSet) {
          const availablePieces = _.sum(_.map(itemSet.items, itemId => (itemIds.has(itemId) ? 1 : 0)));
          if (availablePieces < num) {
            return 0;
          }
          return num;
        }

        // no item set found, probably change in expansion, set the constraint back to 0
        return 0;
      });
    }
    case types.CHOOSE_ITEM_SET_REQUIREMENT:
      return {
        ...state,
        [payload.itemSetId]: payload.num,
      };
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize?.itemSetRequirements || {};
    case types.RESET_OPTIMIZE:
      return {};
    default:
      return state;
  }
};

export default combineReducers({
  text: textReducer,
  preloadData: preloadDataReducer,
  combinations: combinationsReducer,
  gear: gearReducer,
  selected: selectedReducer,
  loading: loadingReducer,
  gemEnchant: gemEnchantReducer,
  lockedRing: lockReducer('rings', 'lockedRing'),
  lockedTrinket: lockReducer('trinkets', 'lockedTrinket'),
  hideLowLevelItems: hideLowLevelItemsReducer,
  talentTrees: talentTreesReducer,
  enhancementAdvancedControls,
  enhancementReplace,
  enhancements: enhancementsReducer,

  download: downloadReducer,

  upgradeCurrencyConstraints,
  catalystCharges: setterReducer({
    set: types.CHOOSE_CATALYST_CHARGES,
    initial: 1,
    reset: types.RESET_OPTIMIZE,
  }),
  itemSetRequirements,
});

const base = state => state.simbot.optimize;

const textSelector = state => base(state).text;
const preloadDataSelector = state => base(state).preloadData;
const baseGearSelector = state => base(state).gear;
const gearSelector = createSelector(
  baseGearSelector,
  gear => _.mapValues(gear, items => {
    // save original index
    return _.map(items, (item, index) => {
      return {
        ...item,
        origIdx: index,
        selectedIdx: index,
        socketInfo: item.socketInfo || itemSocketInfo(item),
      };
    });
  }),
);
const equippedGearSelector = createSelector(
  gearSelector,
  gear => _.mapValues(gear, items => _.filter(items, { equipped: true })),
);
const selectedSelector = state => base(state).selected;
const lockedRingSelector = state => base(state).lockedRing;
const lockedTrinketSelector = state => base(state).lockedTrinket;
const hideLowLevelItemsSelector = state => base(state).hideLowLevelItems;
const sourceSelector = createSelector(textSelector, inputSource);
const dataSelector = createSelector(textSelector, transformInputToData);
const rawGearSelector = createSelector(dataSelector, makeGearOptions);
const validGearSelector = createSelector(gearSelector, isGearValidForOptimize);
const enhancementAdvancedControlsSelector = state => base(state).enhancementAdvancedControls;
const enhancementReplaceSelector = state => base(state).enhancementReplace;
const equippedTalentsSelector = createSelector(
  dataSelector,
  data => {
    const raw = SimcInput.find(data, 'talents');
    return TalentTraits.normalize(raw);
  },
);
const selectedGearSelector = createSelector(
  gearSelector,
  selectedSelector,
  getSelectedGear,
);
const inputInfoSelector = createSelector(
  textSelector,
  inputInfo,
);
const characterSelector = createSelector(
  dataSelector,
  data => {
    if (data) {
      return SimcInput.toCharacter(data);
    }
    return null;
  },
);
const classIdSelector = createSelector(
  characterSelector,
  char => _.get(char, 'class', null),
);
const specSelector = createSelector(
  dataSelector,
  data => {
    return SimcInput.find(data, 'spec');
  },
);
const levelSelector = createSelector(
  characterSelector,
  char => char?.level,
);


const upgradeCurrencyConstraintsSelector = state => base(state).upgradeCurrencyConstraints;
const catalystChargesSelector = state => base(state).catalystCharges;
const itemSetRequirementsSelector = state => base(state).itemSetRequirements;

const rawCombinationsSelector = createSelector(
  selectedGearSelector,
  gemEnchantSelectors.gem,
  gemEnchantSelectors.cloakEnchant,
  gemEnchantSelectors.ringEnchant,
  gemEnchantSelectors.neckEnchant,
  gemEnchantSelectors.weaponEnchant,
  gemEnchantSelectors.weaponOffhandEnchant,
  gemEnchantSelectors.replaceExisting,
  classIdSelector,
  specSelector,
  enhancementsSelectors.selectedEnhancements,
  enhancementReplaceSelector,
  enhancementsSelectors.addSocketNum,
  enhancementsSelectors.addSocketGem,
  upgradeCurrencyConstraintsSelector,
  talentTreeSelectors.talentLoadouts,
  levelSelector,
  catalystChargesSelector,
  getRawCombinations,
);
const loadingSelector = state => state.simbot.optimize.loading;
const dataErrorSelector = createSelector(
  dataSelector,
  data => {
    if (data) {
      const actors = SimcInput.getActors(data);
      if (isUnsupportedHealer(SimcInput.toCharacter(data))) {
        return 'healer';
      }
      if (actors.length === 0) {
        return 'no_actors';
      }
      const invalidCommands = SimcInput.getInvalidCommands(data);
      if (invalidCommands.length > 0) {
        return 'invalid_commands';
      }

      const talents = SimcInput.find(data, 'talents');
      const level = SimcInput.find(data, 'level');
      if (talents) {
        const parsed = TalentTraits.parse(talents);
        const totalPoints = _.sum(_.values(TalentTraits.getPointsForLevel(level)));
        const spentPoints = _.sum(_.values(parsed?.spent)) || 0;
        if (spentPoints > totalPoints) {
          return 'too_many_talents';
        }
      }
    }
    return null;
  },
);

export const selectors = {
  text: textSelector,
  preloadData: preloadDataSelector,
  inputInfo: inputInfoSelector,
  data: dataSelector,
  source: sourceSelector,
  gear: gearSelector,
  selected: selectedSelector,
  selectedGear: selectedGearSelector,
  rawGear: rawGearSelector,
  validGear: validGearSelector,
  dataError: dataErrorSelector,
  rawCombinations: rawCombinationsSelector,
  lockedRing: lockedRingSelector,
  lockedTrinket: lockedTrinketSelector,
  loading: loadingSelector,
  hideLowLevelItems: hideLowLevelItemsSelector,
  enhancementAdvancedControls: enhancementAdvancedControlsSelector,
  enhancementReplace: enhancementReplaceSelector,
  equippedGear: equippedGearSelector,
  equippedTalents: equippedTalentsSelector,
  addSocketNum: enhancementsSelectors.addSocketNum,
  addSocketGem: enhancementsSelectors.addSocketGem,
  classId: classIdSelector,
  spec: specSelector,
  level: levelSelector,
  upgradeCurrencyConstraints: upgradeCurrencyConstraintsSelector,
  catalystCharges: catalystChargesSelector,
  itemSetRequirements: itemSetRequirementsSelector,
  algariColors: enhancementsSelectors.algariColors,
};
